import { History, Location } from 'history';
import { ReactNode } from 'react';
import { ISOWeekDay } from 'utils/getStartAndEndOfWeek';

export interface RouterMatch {
  path: string;
  url: string;
  isExact: boolean;
  params: Record<string, string>;
}

export interface TutorialSectionSeen {
  employees_crews: boolean;
  equipments_devices: boolean;
  customers_jobs: boolean;
  schedule: boolean;
  tracking: boolean;
  invoices: boolean;
  quickbooks: boolean;
  estimates: boolean;
  analytics: boolean;
  routes_odjs: boolean;
  budgeting: boolean;
  budgeting_expense: boolean;
  budgeting_overhead: boolean;
  budgeting_workhours: boolean;
  timesheet_edit_history: boolean;
  edit_timesheet: boolean;
  timesheet_report: boolean;
  employee_timesheet: boolean;
  job_reporting: boolean;
  customers_list: boolean;
  customer_data: boolean;
  sent_draft: boolean;
  automation_setup: boolean;
  templates: boolean;
  online_payment: boolean;
  online_payment_setting: boolean;
  maintenance_setup: boolean;
  overall: boolean;
}

interface SubscriptionPlan {
  Id: number;
  Price: number;
  Name: PlanName;
  DisplayName: string;
  Type: string;
  BillingFrequency: string;
  TrialDays: number;
  GracePeriodDays: number;
  IsActive: boolean;
  CompanyId: number;
}

interface SubscriptionAddon {
  Id: number;
  Name: string;
  Price: number;
}

interface SubscriptionPromoCodeUsed {
  ToggleAddonsFree: boolean | null;
  Id: number;
  DiscountRate: number;
  DiscountAmount: number;
  Value: number;
  Code: string;
  Duration: string;
  DurationDays: number;
  PlanId: number;
  Used: boolean;
  MultiUse: boolean;
  IsActive: boolean;
  IsFleetPromo: boolean;
  PromocodeType: string;
  CreatedBy: string;
  UpdatedBy: string | null;
  CreatedOn: string;
  UpdatedOn: string;
  DeletedOn: string | null;
}

interface SubscriptionDetails {
  Id: number;
  RemainingTrialDays: number | null;
  RemainingGracePeriodDays: number | null;
  PlanId: number;
  promoCodeUsed: SubscriptionPromoCodeUsed | null;
  Plan: SubscriptionPlan;
  Type: string;
  StartDate: string;
  NextBillingDate: string;
  Device: unknown | null;
  TrueStatusId: number;
  PreviousStatus: number;
  NextPlanId: number | null;
  GraceWarning: boolean;
  IsFleetSubscription: boolean;
  CompanyId: number;
  SubscriptionStatusId: number;
  SubscribedAddons: unknown[];
  Addons: SubscriptionAddon[];
  IncludedAddons: SubscriptionAddon[];
  NextPlanAddons: SubscriptionAddon[];
  NextPlanPriceWithAddon: number;
  NextPlanPrice: number;
}

export interface CompanyOwner {
  UserId: string;
  FirstName: string;
  LastName: string;
  EmailAddress: string;
  PhoneNumber: string;
  Landline: string;
  RoleId: number;
  Password: string | null;
  AppLanguage: string;
  IsActive: boolean;
  CreatedBy: string;
  UpdatedBy: string | null;
  CreatedOn: string;
  UpdatedOn: string;
  DeletedOn: string | null;
  Operator: {
    Id: string;
    FirstName: string;
    LastName: string;
    PhoneNumber: string;
    Landline: string;
    IsOnboard: boolean;
    UserId: string;
    IsActive: boolean;
    CreatedBy: string;
    UpdatedBy: string | null;
    Timezone: string;
    CreatedOn: string;
    UpdatedOn: string;
    DeletedOn: string | null;
  };
}

export interface CompanyOneTimePopups {
  Id: number;
  CompanyId: number;
  UserId: string;
  ModalName: string;
  Shown: boolean;
}

export interface CompanyInvoiceFrequency {
  Id: number;
  CompanyId: number;
  UserId: string;
  ModalName: string;
  Shown: boolean;
}

export interface CompanyInvoiceTerm {
  ResidentialTerms: {
    DueDays: number;
  };
  CommercialTerms: {
    DueDays: number;
  };
  QbTerms: {
    Name: string;
    Active: boolean;
    Type: string;
    DueDays: number;
    DiscountDays: number;
    domain: string;
    sparse: boolean;
    Id: string;
    SyncToken: string;
    MetaData: {
      CreateTime: string;
      LastUpdatedTime: string;
    };
  };
  CompanyInvoiceTermsId: string;
  CompanyId: number;
  IsQbEnabled: boolean;
  CreatedOn: string;
  UpdatedOn: string;
}

export enum PlanName {
  ESSENTIAL = 'PlanA',
  BASIC = 'PlanH',
  PREMIUM = 'PlanB',
  UNLIMITED = 'PlanC'
}

export const planPrecedence: { [key: string]: number } = {
  [PlanName.ESSENTIAL]: 0,
  [PlanName.BASIC]: 1,
  [PlanName.PREMIUM]: 2,
  [PlanName.UNLIMITED]: 3
};

export interface SubscriptionFreePeriods {
  Id: number;
  CompanyId: number;
  PlanId: number;
  TrialDays: number;
  GracePeriodDays: number;
  Status: string | null;
  CreatedBy: string;
  UpdatedBy: string | null;
  CreatedOn: string;
  UpdatedOn: string;
  DeletedOn: string | null;
  Plan: {
    Id: number;
    Price: number;
    Name: PlanName;
    DisplayName: 'Essential' | 'Basic' | 'Premium' | 'Unlimited';
    Type: 'Platform';
    BillingFrequency: '1M';
    TrialDays: number;
    GracePeriodDays: number;
    IsActive: boolean;
    CompanyId: number;
  };
}

export interface GlobalState {
  currentPermanentFormModal: string | null;
  currentFormModal: string | null;
  showFormCloseWarning: boolean;
  calendarStartDate: string | null;
  calendarEndDate: string | null;
  history: History;
  location: Location;
  match: RouterMatch;
  UserId: string;
  IsAdmin: number;
  CompanyId: number;
  CompanyName: string;
  SubscriptionStatusId: number;
  IsCompanyVerified: number;
  Latitude: number;
  Longitude: number;
  FirstName: string;
  LastName: string;
  RoleId: 1;
  Role: 'Admin';
  EmailAddress: string;
  PhoneNumber: string | null;
  Subscription: unknown;
  TutorialSectionSeen: TutorialSectionSeen;
  cognitoTokens: {
    accessToken: string;
    idToken: string;
  };
  isAdmin: 0 | 1;
  loggedAsAdmin: 0 | 1;
  companyId: number;
  userId: string;
  firstName: string;
  lastName: string;
  companyName: string;
  StartWorkHour: string;
  EndWorkHour: string;
  Holidays: boolean;
  Weekends: boolean;
  StartOfWeek: ISOWeekDay;
  tutorialSection: TutorialSectionSeen;
  subscriptionDetails: SubscriptionDetails;
  isAuthenticated: boolean;
  Quickbooks: boolean;
  DisplayName: string;
  Sender: string;
  ReplyTo: string;
  MonthlyInsuranceCosts: unknown;
  City: string;
  County: string;
  State: string;
  Country: string | null;
  MonthlyFuelCosts: unknown;
  AddressLine1: string;
  AddressLine2: string;
  CustomerData: unknown;
  PostalCode: string;
  AdditionalNotes: string | null;
  CreatedBy: string;
  UpdatedBy: string;
  IsActive: boolean;
  IsTestCompany: boolean;
  HasEquipmentBatteryEnabled: boolean;
  HasChargingAtSpecifiedTimeEnabled: boolean;
  ChargingAtSpecifiedTime: unknown;
  IsTrackingEnabled: boolean;
  RealmId: string;
  QuickbooksAccessToken: string;
  QuickbooksRefreshToken: string;
  QuickbooksConnectDate: string;
  QuickbooksCompanyName: string;
  QbTaxAgencyId: string;
  QbTaxRateId: string | null;
  QbTaxCodeId: string | null;
  CrewLatencyMinute: number | null;
  LastSyncOn: string;
  CompanyOwner: CompanyOwner;
  CrewLatencyEnable: boolean;
  IsVerified: number;
  QBRefreshTokenFailure: number;
  IsQbSoftDisconnected: boolean;
  PucNotificationEnabled: boolean;
  CreatedOn: string;
  UpdatedOn: string;
  DeletedOn: string | null;
  CompanyOneTimePopups: CompanyOneTimePopups[];
  CompanyInvoiceFrequency: CompanyInvoiceFrequency;
  CompanyInvoiceTerm: CompanyInvoiceTerm;
  CompanyAddonToggles: [];
  SubscriptionFreePeriods: SubscriptionFreePeriods[];
  PhoneDetails: {
    PhoneNumber: string;
    Landline: string;
  };
  PucDeviceCount: number;
  companyOwnerId: string;
  Evo: unknown;
  companyAddressCoordinates: {
    lat: number;
    lng: number;
  };
  numberOfCreditCards: number;
  countBossDevices: number;
  isSmartDeviceConnected: boolean;
  showAppTour: boolean;
  Timezone: string;
  tutorial: TutorialSectionSeen;
  subscriptionStatusId: number;
  timezone: string;
  EvoStatus: number;

  showFormModal: (form: ReactNode) => void;
  closeFormModal: () => void;
  showPermanentFormModal: (form: ReactNode) => void;
  closePermanentFormModal: () => void;
  toggleFormCloseWarning: (value?: boolean) => void;
}
